import React, { useRef } from 'react';

const SignupPersonalInfo = ({ formData, errors, handleChange, nextStep, handleFileUpload }) => {
  const fileInputRef = useRef(null);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleRemoveImage = () => {
    handleFileUpload(null);
  };

  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">Personal Information</h3>
        <p className="text-gray-600">Tell us about yourself</p>
      </div>

      {/* Profile Picture Upload Section */}
      <div className="group">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Profile Picture
        </label>
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div 
              className={`w-32 h-32 rounded-2xl border-2 border-dashed cursor-pointer transition-all duration-300 flex items-center justify-center overflow-hidden ${
                formData.personal.profilePicture 
                  ? 'border-green-500 bg-green-50' 
                  : errors['personal.profilePicture']
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 bg-gray-50 hover:border-green-400 hover:bg-green-50'
              }`}
              onClick={handleImageClick}
            >
              {formData.personal.profilePicture ? (
                <>
                  <img 
                    src={formData.personal.profilePicture} 
                    alt="Profile preview" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </>
              ) : (
                <div className="text-center p-4">
                  <svg className={`w-12 h-12 mx-auto mb-2 ${
                    errors['personal.profilePicture'] ? 'text-red-400' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className={`text-xs ${
                    errors['personal.profilePicture'] ? 'text-red-500' : 'text-gray-500'
                  }`}>
                    Click to upload
                  </p>
                  <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                </div>
              )}
            </div>
            
            {formData.personal.profilePicture && (
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors duration-200 shadow-lg"
              >
                ×
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          <div className="text-center">
            <button
              type="button"
              onClick={handleImageClick}
              className={`text-sm font-medium transition-colors duration-200 ${
                errors['personal.profilePicture'] 
                  ? 'text-red-600 hover:text-red-700' 
                  : 'text-green-600 hover:text-green-700'
              }`}
            >
              {formData.personal.profilePicture ? 'Change Photo' : 'Upload Photo'}
            </button>
          </div>

          {formData.personal.profilePicture ? (
            <div className="text-center">
              <p className="text-xs text-green-600 font-medium">
                ✓ Photo uploaded successfully
              </p>
            </div>
          ) : errors['personal.profilePicture'] && (
            <div className="text-center">
              <p className="text-xs text-red-600 font-medium">
                {errors['personal.profilePicture']}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="group">
          <label htmlFor="personal.firstName" className="block text-sm font-semibold text-gray-700 mb-2">
            First Name *
          </label>
          <input
            id="personal.firstName"
            name="personal.firstName"
            type="text"
            required
            value={formData.personal.firstName}
            onChange={handleChange}
            className={`w-full px-4 py-3 text-base border-2 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm ${
              errors['personal.firstName'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
            }`}
            placeholder="John"
          />
          {errors['personal.firstName'] && (
            <p className="mt-1 text-sm text-red-600">{errors['personal.firstName']}</p>
          )}
        </div>

        <div className="group">
          <label htmlFor="personal.lastName" className="block text-sm font-semibold text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            id="personal.lastName"
            name="personal.lastName"
            type="text"
            required
            value={formData.personal.lastName}
            onChange={handleChange}
            className={`w-full px-4 py-3 text-base border-2 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm ${
              errors['personal.lastName'] 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
            }`}
            placeholder="Doe"
          />
          {errors['personal.lastName'] && (
            <p className="mt-1 text-sm text-red-600">{errors['personal.lastName']}</p>
          )}
        </div>
      </div>

      <div className="group">
        <label htmlFor="personal.email" className="block text-sm font-semibold text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          id="personal.email"
          name="personal.email"
          type="email"
          required
          value={formData.personal.email}
          onChange={handleChange}
          className={`w-full px-4 py-3 text-base border-2 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm ${
            errors['personal.email'] 
              ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
              : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
          }`}
          placeholder="your@email.com"
        />
        {errors['personal.email'] && (
          <p className="mt-1 text-sm text-red-600">{errors['personal.email']}</p>
        )}
      </div>

      <div className="group">
        <label htmlFor="personal.phone" className="block text-sm font-semibold text-gray-700 mb-2">
          Phone Number *
        </label>
        <input
          id="personal.phone"
          name="personal.phone"
          type="tel"
          required
          value={formData.personal.phone}
          onChange={handleChange}
          className={`w-full px-4 py-3 text-base border-2 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm ${
            errors['personal.phone'] 
              ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
              : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
          }`}
          placeholder="+1 (555) 123-4567"
        />
        {errors['personal.phone'] && (
          <p className="mt-1 text-sm text-red-600">{errors['personal.phone']}</p>
        )}
      </div>

      <div className="group">
        <label htmlFor="personal.bio" className="block text-sm font-semibold text-gray-700 mb-2">
          Bio
        </label>
        <textarea
          id="personal.bio"
          name="personal.bio"
          rows="3"
          value={formData.personal.bio}
          onChange={handleChange}
          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all duration-300 bg-white shadow-sm resize-none"
          placeholder="Tell us about yourself..."
        />
      </div>

      <button
        type="button"
        onClick={nextStep}
        className="w-full py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
      >
        Next: Professional Information
      </button>

     
    </div>
  );
};

export default SignupPersonalInfo;